import React, { useState, useMemo } from 'react'
import Button from 'reactstrap/es/Button'
import { Popover, Modal, Input } from 'antd'
import { Link } from 'react-router-dom'
import _ from 'lodash'

// Tổng hợp các lý do KHÔNG thể tạo lô ship từ danh sách thùng đang chọn.
// Nút bị disable nếu vướng bất kỳ điều kiện nào dưới đây.
export const getBatchShipDisableInfo = (selectedItems = []) => {
    // "Kết thúc cập nhật thông tin" chỉ áp dụng cho thùng FBA, thùng thường (HW...) không phải qua bước này
    const notFinishFba = selectedItems.filter(box => !!_.get(box, 'is_fba_box') && _.get(box, 'is_finish_update_fba_info') !== true)
    const notClosed = selectedItems.filter(box => _.get(box, 'status') !== 'closed')
    const sbFast = selectedItems.filter(box => _.get(box, 'shipping_carrier') === 'SB_FAST')
    const alreadyInBatch = selectedItems.filter(box => !!_.get(box, 'batch_ship'))
    // Điều kiện "cùng 1 đơn hàng" chỉ áp dụng cho thùng FBA
    const fbaBoxes = selectedItems.filter(box => !!_.get(box, 'is_fba_box'))
    const orderNumbers = [...new Set(fbaBoxes.map(box => _.get(box, 'orderNumber')))]
    const notSameOrder = orderNumbers.length > 1

    const disabled = notFinishFba.length > 0
        || notClosed.length > 0
        || sbFast.length > 0
        || alreadyInBatch.length > 0
        || notSameOrder

    return { notFinishFba, notClosed, sbFast, alreadyInBatch, fbaBoxes, orderNumbers, notSameOrder, disabled }
}

const MODAL_REASON = {
    notClosed: <>chưa ở trạng thái <strong>Đã đóng</strong></>,
    notFinishFba: <>chưa kết thúc cập nhật thông tin</>,
    sbFast: <>có shipping carrier <strong>SB_FAST</strong></>,
    alreadyInBatch: <>đã được tạo lô ship</>,
    notSameOrder: <>không cùng thuộc 1 đơn hàng</>,
}

// Nội dung popover: mỗi lý do kèm số lượng, click vào số để xem danh sách thùng tương ứng
const renderTooltipContent = (info, openModalFn) => (
    <div className="BatchShipReason">
        {info.notClosed.length > 0 && (
            <div>
                <span className="cursor-pointer text-primary" onClick={() => openModalFn('notClosed')}>
                    {info.notClosed.length}
                </span>
                {' thùng '}{MODAL_REASON.notClosed}
            </div>
        )}
        {info.notFinishFba.length > 0 && (
            <div>
                <span className="cursor-pointer text-primary" onClick={() => openModalFn('notFinishFba')}>
                    {info.notFinishFba.length}
                </span>
                {' thùng '}{MODAL_REASON.notFinishFba}
            </div>
        )}
        {info.sbFast.length > 0 && (
            <div>
                <span className="cursor-pointer text-primary" onClick={() => openModalFn('sbFast')}>
                    {info.sbFast.length}
                </span>
                {' thùng '}{MODAL_REASON.sbFast}
            </div>
        )}
        {info.alreadyInBatch.length > 0 && (
            <div>
                <span className="cursor-pointer text-primary" onClick={() => openModalFn('alreadyInBatch')}>
                    {info.alreadyInBatch.length}
                </span>
                {' thùng '}{MODAL_REASON.alreadyInBatch}
            </div>
        )}
        {info.notSameOrder && (
            <div className="cursor-pointer text-primary" onClick={() => openModalFn('notSameOrder')}>
                Các thùng đã chọn không thuộc cùng 1 đơn hàng
            </div>
        )}
    </div>
)

// Gom các thùng đang chọn theo đơn hàng -> [{ orderNumber, boxes }]
const buildOrderGroups = (selectedItems = []) => {
    const map = {}
    selectedItems.forEach(box => {
        const key = _.get(box, 'orderNumber') || '(Không có đơn hàng)'
        if (!map[key]) map[key] = []
        map[key].push(box)
    })
    return Object.keys(map)
        .sort((a, b) => a.localeCompare(b))
        .map(orderNumber => ({ orderNumber, boxes: map[orderNumber] }))
}

const renderBoxTile = (box, showOrder) => (
    <Link
        key={box._id}
        to={box.is_fba_box ? `/next-boxes-fba/${box._id}` : `/next-boxes/${box._id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="BatchShipBox__tile px-2 py-1 border rounded text-truncate"
        title={`#${box.id}${box.orderNumber ? ` - ${box.orderNumber}` : ''}`}
    >
        #{box.id}
        {showOrder && box.orderNumber && (
            <span className="BatchShipBox__tile-order text-truncate">{box.orderNumber}</span>
        )}
    </Link>
)

const matchBox = (box, term) =>
    String(_.get(box, 'id', '')).toLowerCase().includes(term)
    || String(_.get(box, 'orderNumber', '')).toLowerCase().includes(term)

const sumBoxes = (groups) => groups.reduce((total, group) => total + group.boxes.length, 0)

const BoxListModal = ({ modalType, boxes = [], orderGroups = [], onClose }) => {
    const isOrderMode = modalType === 'notSameOrder'
    const [searchTerm, setSearchTerm] = useState('')

    // Order mode: lọc theo mã thùng hoặc orderNumber, chỉ giữ nhóm còn thùng khớp
    const filteredGroups = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()
        if (!term) return orderGroups
        return orderGroups
            .map(group => {
                if (String(group.orderNumber || '').toLowerCase().includes(term)) return group
                const matched = group.boxes.filter(box => matchBox(box, term))
                return matched.length ? { ...group, boxes: matched } : null
            })
            .filter(Boolean)
    }, [orderGroups, searchTerm])

    const filteredBoxes = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()
        if (!term) return boxes
        return boxes.filter(box => matchBox(box, term))
    }, [boxes, searchTerm])

    // Tiêu đề luôn đếm theo số THÙNG cho nhất quán với 4 lý do; order mode thêm số đơn
    const boxCount = isOrderMode ? sumBoxes(filteredGroups) : filteredBoxes.length
    const title = isOrderMode
        ? <>Danh sách thùng theo đơn hàng ({boxCount} thùng - {filteredGroups.length} đơn) - {MODAL_REASON[modalType]}</>
        : <>Danh sách thùng ({boxCount}) - {MODAL_REASON[modalType]}</>

    const isEmpty = isOrderMode ? filteredGroups.length === 0 : filteredBoxes.length === 0

    return (
        <Modal
            visible={!!modalType}
            wrapClassName="BatchShipBoxModal"
            title={title}
            onCancel={onClose}
            footer={<Button color="secondary" onClick={onClose}>Đóng</Button>}
            width={760}
        >
            <Input.Search
                allowClear
                placeholder="Tìm kiếm mã thùng / đơn hàng..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mb-3"
            />
            {isEmpty ? (
                <div className="text-center text-muted py-4">Không tìm thấy kết quả phù hợp</div>
            ) : (
                <div className="BatchShipBox__scroll">
                    {isOrderMode
                        ? filteredGroups.map(group => (
                            <div key={group.orderNumber} className="BatchShipBox__group">
                                <div className="BatchShipBox__group-title">
                                    {group.orderNumber}
                                    <span className="BatchShipBox__group-count">({group.boxes.length} thùng)</span>
                                </div>
                                <div className="BatchShipBox__grid">
                                    {group.boxes.map(box => renderBoxTile(box, false))}
                                </div>
                            </div>
                        ))
                        : <div className="BatchShipBox__grid">{filteredBoxes.map(box => renderBoxTile(box, true))}</div>
                    }
                </div>
            )}
        </Modal>
    )
}

const BatchShipBulkAction = ({ selectedItems = [], onCreateBatchShip }) => {
    const [modalType, setModalType] = useState(null)

    const info = getBatchShipDisableInfo(selectedItems)

    if (!info.disabled) {
        return (
            <Button color="link" className="mr-1" onClick={onCreateBatchShip}>
                Tạo lô ship
            </Button>
        )
    }

    const isOrderModal = modalType === 'notSameOrder'
    const modalBoxes = isOrderModal ? [] : (info[modalType] || [])
    const orderGroups = isOrderModal ? buildOrderGroups(info.fbaBoxes) : []

    return (
        <span className="BatchShipBulkAction">
            <span className="BatchShipBulkAction__disabled">
                <Button color="link" className="mr-1" disabled>
                    Tạo lô ship
                </Button>
            </span>
            <Popover
                content={renderTooltipContent(info, setModalType)}
                title="Lý do không thể tạo lô ship"
                placement="top"
                trigger="hover"
            >
                <i className="BatchShipBulkAction__icon fa fa-info-circle text-info cursor-pointer" />
            </Popover>
            {!!modalType && (
                <BoxListModal
                    modalType={modalType}
                    boxes={modalBoxes}
                    orderGroups={orderGroups}
                    onClose={() => setModalType(null)}
                />
            )}
        </span>
    )
}

export default BatchShipBulkAction
